// Score page copy and scoring logic, ported exactly from the DIGU Site Reference artifact.
import { ROUTES } from './routes';
import { DOMAINS } from './domains';

export const SCORE_PAGE = {
  hero: {
    eyebrow: 'Free score',
    title: 'Score your organization in ten questions',
    lede: 'Two minutes. No call, no sales conversation. You get a profile across the five control domains, your floor, and a view of what to fix first.',
    note: {
      lead: 'Answer honestly.',
      text: ' "Not sure" scores the same as no. If you cannot show the evidence, an auditor will not count it either, so neither do we.',
    },
  },
};

// Each question names its domain, and whether "yes" or "no" is the good answer.
export const QUESTIONS = [
  { d: 'D1', t: "Does Claude use its own identity when it touches a system, rather than a developer's credentials?", good: 'yes' },
  { d: 'D1', t: 'Could an agent reach a credential today that grants write access to production?', good: 'no', hint: 'Here, no is the good answer.' },
  { d: 'D2', t: 'Are there organization wide settings your developers cannot override?', good: 'yes' },
  { d: 'D2', t: 'Does anything an agent does in production require a human approval?', good: 'yes' },
  { d: 'D3', t: 'Is there a written definition of which content counts as untrusted?', good: 'yes' },
  { d: 'D3', t: 'Can outside input, a ticket or an issue, trigger something automated that runs an agent?', good: 'no', hint: 'Here, no is the good answer.' },
  { d: 'D4', t: 'Is there a single approved list of what Claude is allowed to connect to?', good: 'yes' },
  { d: 'D4', t: 'When Claude reads an internal system, does it see only what that individual user is allowed to see?', good: 'yes' },
  { d: 'D5', t: "In your monitoring, can you tell an agent's action from a person's?", good: 'yes' },
  { d: 'D5', t: 'If you had to stop everything today, do you know how, and has anyone tested it?', good: 'yes' },
];

// One fix per question, in question order. Defined in the artifact but not yet shown in the result.
export const FIXES = [
  "Give the agent its own identity, separate from any developer's credentials.",
  'Remove long lived credentials from anywhere the agent can reach.',
  'Deploy organization wide settings your developers cannot override.',
  'Put a human approval in front of anything an agent does in production.',
  'Write down which content counts as untrusted, in one page.',
  'Review and gate every automated workflow that outside input can trigger.',
  'Publish one approved list of what Claude may connect to.',
  "Map internal connectors to the individual user's own permissions.",
  "Send agent activity to your monitoring, attributable and distinguishable from a person's.",
  'Document a kill switch, then rehearse it.',
];

export const ANSWER_OPTIONS = [
  { value: 'yes', label: 'Yes', variant: 'primary' },
  { value: 'no', label: 'No', variant: 'ghost' },
  { value: 'unsure', label: 'Not sure', variant: 'ghost' },
];

export const SCORER_COPY = {
  footIntro: 'Ten questions. Nothing is sent anywhere.',
  footQuestion: '"Not sure" scores the same as no. Nothing is sent anywhere.',
  footGate: 'Two fields. We take the company from your email domain rather than asking for it.',
  footResult: 'Ten questions answered.',
  restart: 'Start again',
  questionOf: (n, total) => `Question ${n} of ${total}`,
  gate: {
    eyebrow: 'Ten of ten answered',
    title: 'Where should we send your results?',
    emailLabel: 'Work email',
    emailPlaceholder: 'name@company.com',
    bandLabel: 'Number of developers',
    bands: ['Under 25', '25 to 50', '50 to 150', '150 to 300', 'Over 300'],
    defaultBand: '50 to 150',
    hint: 'We send results to a work address. It keeps the report out of a personal inbox. One email with the full report, no sequence, unsubscribe in one click.',
    submit: 'Show my results',
  },
  result: {
    eyebrow: 'Your profile',
    average: 'Average',
    floor: 'Floor',
    floorLine: (floor, names) => ({ before: 'Your floor is ', em: `level ${floor}`, after: ` in ${names}.` }),
    actOn:
      'That is the number to act on. A high average across four domains does not help you if an attacker only needs the fifth.',
    indicative:
      'This is indicative. It is ten questions, not the thirty two control assessment, and nothing here has been checked against your evidence.',
    actions: [
      { label: 'Send me the full profile', to: ROUTES.contact },
      { label: 'See the assessment', to: ROUTES.assessment, variant: 'ghost' },
    ],
  },
};

export const VERDICTS = {
  0: 'Claude has access today and nothing is enforcing how it is used. This is the most common result, and it is the cheapest one to fix. Most of the work is configuration, not paperwork.',
  1: 'The obvious controls are in place. What is missing is the written trust boundary, the approval step, and the connection allowlist. That is usually three to four weeks of work.',
  2: 'You are ahead of most teams. The gap is almost always evidence. You can probably do the work yourselves, and we will say so on a call rather than sell you something you do not need.',
};

export const verdictFor = (floor) => (floor === 0 ? VERDICTS[0] : floor === 1 ? VERDICTS[1] : VERDICTS[2]);

// Indicative mapping. Ten questions cannot see all 32 controls, so a domain
// is credited only for the levels its questions actually cover.
export function scoreLevels(answers) {
  const pass = (i) => answers[i] === QUESTIONS[i].good;
  const L = {};
  L.D1 = pass(0) && pass(1) ? 1 : 0;
  L.D2 = pass(2) ? (pass(3) ? 2 : 1) : 0;
  const d3 = (pass(4) ? 1 : 0) + (pass(5) ? 1 : 0);
  L.D3 = d3 === 2 ? 2 : d3 === 1 ? 1 : 0;
  L.D4 = pass(6) ? (pass(7) ? 3 : 1) : 0;
  L.D5 = pass(9) ? (pass(8) ? 3 : 2) : 0;
  return L;
}

export function scoreSummary(answers) {
  const levels = scoreLevels(answers);
  const vals = DOMAINS.map((d) => levels[d.id]);
  const floor = Math.min(...vals);
  const average = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  const lowest = DOMAINS.filter((d) => levels[d.id] === floor);
  const lowestNames = lowest.map((d) => d.name.toLowerCase()).join(' and ');
  return { levels, floor, average, lowest, lowestNames, verdict: verdictFor(floor) };
}
